import { connectHits, connectSearchBox, connectClearRefinements } from 'instantsearch.js/es/connectors';
import { CategoriesOrder, ICcHit, IWidgetParams } from '../types';
import { searchBox, menu, clearRefinements } from 'instantsearch.js/es/widgets';
import { HitsConnectorParams } from 'instantsearch.js/es/connectors/hits/connectHits';
import { formatDate } from '../../../utils/utils';
import { MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu';

type GroupedByParent = {
  parentTitle: string;
  parentId: string;
  parentTitleNumber: string;
  parentOrder: number;
  parentPdf?: string;
  hits: Array<ICcHit>
}

type GroupedHits = Array<{
  label: string;
  order: number;
  hitsPerParent: Array<GroupedByParent>;
}>;

const DOC_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14">' +
  '<path d="M4 0.00212002V0H11.3319C11.7009 0 12 0.30354 12 0.6612V12.6721C12 13.0373 11.7034 13.3333 11.3377 13.3333H0.662267C0.296507 13.3333 0 13.0334 0 12.6621V4L4 0.00212002ZM1.88612 4H4V1.88724L1.88612 4ZM5.33333 1.33333V4.66667C5.33333 5.03485 5.03487 5.33333 4.66667 5.33333H1.33333V12H10.6667V1.33333H5.33333Z" fill="black"/>' +
  '</svg>';

// Hits
const getConventionCollectiveHitsWidgets = (container: HTMLElement) => {
  const customHits = connectHits((renderOptions, isFirstRender: boolean) => {
    const { items, widgetParams, results } = renderOptions;
    const classExpand = results?.query && results?.query !== '*' ? 'expanded'  : '';
    let groupedItems: GroupedHits = [];
    let hitsContent = '';


    if (isFirstRender) {
      // Do some initial rendering and bind events
    }

    if (items.length) {
      const byCategory = Map.groupBy(items, (item) => item.category_label);

      groupedItems = [...byCategory.entries()]
        .map(([label, categoryItems]) => {
          // Grouper par parent_id au sein de chaque catégorie
          const byParent = Map.groupBy(categoryItems, (item) => item.parent_id);
          const isAccordGroup = categoryItems.some((item) => item.content_type === 'accord');
          const sortFunc = (contentType?: string) => {
            if (contentType === 'accord') {
              return (a: ICcHit, b: ICcHit) => b.order - a.order;
            }
            return (a: ICcHit, b: ICcHit) => a.order - b.order;
          };
          const hitsPerParent: GroupedByParent[] = [...byParent.entries()]
            .map(([parentId, parentItems]) => {
              const base: ICcHit = parentItems[0] as unknown as ICcHit;
              const result: GroupedByParent = {
                parentId: `${parentId}`,
                parentTitle: base.parent_title,
                parentOrder: base.parent_order,
                parentTitleNumber: base.parent_title_number,
                parentPdf: base.parent_pdf,
                // Trier les items par "order"
                // inverse pour accord
                hits: (parentItems as Array<unknown> as Array<ICcHit>).sort(sortFunc(isAccordGroup ? 'accord' : undefined)),
              };

              return result;
            })
            // Trier les groupes par "parent_order"
            .sort((a, b) => a.parentOrder - b.parentOrder);

          return {
            label,
            order: categoryItems[0].category_order,
            hitsPerParent,
          };
        })
        // Trier les catégories par "category_order"
        .sort((a, b) => a.order - b.order);

      // Content
      hitsContent = '';

      // Icon +
      const iconEl = `
        <div class="icon-wrapper">
           <div class="icon-expand" aria-hidden="true" focusable="false">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12,22C6.477,22 2,17.523 2,12C2,6.477 6.477,2 12,2C17.523,2 22,6.477 22,12C22,17.523 17.523,22 12,22Z" style="fill:rgb(174,18,160);fill-rule:nonzero;"/>
                  <rect class="plus-vertical" x="11" y="7" width="2" height="10" style="fill:white;"/>
                  <rect class="plus-horizontal" x="7" y="11" width="10" height="2" style="fill:white;"/>
              </svg>  
            </div>         
        </div>
      `;
      groupedItems.forEach(({ label: categoryLabel, hitsPerParent }) => {
        const FILTERS_PLACEHOLDER = `<!-- ${categoryLabel} -->`;
        let thematicsFilters: string[] = [];
        hitsContent += '<convention-category class="cc-category">';
        hitsContent += `<h4 class="cc-category-label">${categoryLabel}</h4>`;
        hitsContent += '<div class="cc-parents-container">';
        hitsContent += FILTERS_PLACEHOLDER;

        for (const parent of hitsPerParent){
          const hasParent = parent.parentId !== '0';
          let childrenThematics: string[] = [];
          // Cas des articles par exemple
          if (hasParent) {
            let childrenContent = '';
            if (parent.hits.length) {
              for (const child of parent.hits) {
                childrenThematics = [...childrenThematics, ...child.thematics];
                childrenContent += `<a href="${child.permalink}" class="collapsible-box-content__child">`;
                childrenContent += `<div class="title-num">${child.content_number}</div>`;
                childrenContent += `<div class="title">${child.title}</div>`;
                childrenContent += '<div class="icon-wrapper"></div>';
                childrenContent += '</a>';
              }
            }
            thematicsFilters = [...thematicsFilters, ...childrenThematics];
            hitsContent += `
            <collapsible-box class="convention-collective ${classExpand}" data-thematics="${Array.from(new Set(childrenThematics)).join(',')}">
              <button class="collapsible-box-button no-underlined">
                <div class="title-wrapper">
                <p class="title-num">${parent.parentTitleNumber}</p>
                <h2 class="title">${parent.parentTitle}</h2>
                </div>
                ${iconEl}
              </button>
              <div class="collapsible-box-content">
                <div class="collapsible-box-content__children-wrapper">
                  ${childrenContent}     
                </div>
              </div>
            </collapsible-box>
          `;
          } else {
            let childrenThematics: string[] = [];
            for (const hit of parent.hits) {
              const hasDocs = hit.convention_docs_files?.length > 0;
              const applicationDate = hit.convention_date > 0 ? `<p class="application-date">Date d’application : ${formatDate(hit.convention_date * 1000)}</p>` : '';
              // On affiche les filtres pour les convetions de type accord seulement
              if (hit.content_type === 'accord') childrenThematics = [...childrenThematics, ...hit.thematics];

              if (hasDocs) {
                let docEls = '<ul>';
                for (const doc of hit.convention_docs_files) {
                  docEls += '<li>';
                  docEls += `<a href="${doc.url}" rel="nofollow" download>${doc.title}&nbsp;&nbsp;<span>${DOC_ICON}</span></a>`;
                  docEls += '</li>';
                }
                docEls += '</ul>';

                hitsContent += `
                <collapsible-box class="convention-collective ${classExpand}" data-thematics="${hit.thematics.join(',')}">
                  <button class="collapsible-box-button no-underlined no-child">
                      <div class="title-wrapper">
                        <p class="title-num">${hit.content_number}</p>
                        <div class="title">
                          <h2>${hit.title}</h2>
                          ${applicationDate}
                        </div>
                      </div>
                      ${iconEl}
                  </button>
                  <div class="collapsible-box-content">
                    <div class="collapsible-box-content__children-wrapper">
                      <div class="collapsible-box-content__child">
                        <div class="title-num"></div>
                        <div class="docs">${docEls}</div>
                        <div class="icon-wrapper"></div>
                      </div>
                    </div>
                  </div>
                </collapsible-box>
              `;
              } else {
                hitsContent += `
                <collapsible-box class="convention-collective" data-thematics="${hit.thematics.join(',')}">
                      <a href="${hit.permalink}" class="collapsible-box-button link no-child">
                          <div class="title-wrapper">
                          <p class="title-num">${hit.content_number}</p>
                          <h3 class="title">${hit.title}</h3>
                          </div>
                      </a>
                </collapsible-box>
              `;
              }
            }
            thematicsFilters = [...thematicsFilters, ...childrenThematics];
          }
        }

        hitsContent += '</div>';
        hitsContent += '</convention-category>';

        // Filters
        if (thematicsFilters.length) {
          const thematics = Array.from(new Set(thematicsFilters));
          let thematicsButtons = '';

          for (const label of thematics) {
            thematicsButtons += `<button class="filter-button" data-label="${label}">${label}</button>`;
          }
          const filtersHtml = `
            <listing-filters>
               <div class="label">Filtrer par : </div>
               <div class="filters-buttons">
                  ${thematicsButtons}
              </div>
            </listing-filters>
          `;
          hitsContent = hitsContent.replace(FILTERS_PLACEHOLDER, filtersHtml);
        }

      });
    } else {
      if (results?.query) {
        hitsContent = `<div class="no-result">Aucun résultat trouvé pour "<strong>${results.query}</strong>"</div>`;
      }
    }

    (widgetParams as IWidgetParams).container.innerHTML = hitsContent;
  });

  return customHits({ container } as HitsConnectorParams);
};

// Search input (custom widget built with connectSearchBox)
const getConventionCollectiveSearchBoxWidgets = (container: HTMLElement, placeholder = 'Rechercher dans la convention...') => {
  const customSearchBox = connectSearchBox((renderOptions: any, isFirstRender: boolean) => {
    const { query, refine, clear, isSearchStalled } = renderOptions;

    // on first render, build DOM structure
    if (isFirstRender) {
      const wrapperEl = document.createElement('div');
      wrapperEl.className = 'ais-SearchBox';

      const form = document.createElement('form');
      form.setAttribute('role', 'search');
      form.classList.add('ais-SearchBox-form');
      form.noValidate = true;
      form.setAttribute('data-gtm-form-interact-id', '0');
      
      const input = document.createElement('input');
      input.className = 'ais-SearchBox-input';
      input.type = 'search';
      input.placeholder = placeholder;
      input.autocomplete = 'off';
      input.autocorrect = 'off';
      input.name = 'search';
      input.autocapitalize = 'off';
      input.spellcheck = false;
      input.maxLength = 512;
      input.setAttribute('aria-label', 'Rechercher');
      input.setAttribute('data-gtm-form-interact-id', '0');

      const submit = document.createElement('button');
      submit.className = 'ais-SearchBox-submit';
      submit.type = 'submit';
      submit.title = 'Rechercher';
      submit.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><path d="M13.5233 12.4628L16.7355 15.6742L15.6742 16.7355L12.4628 13.5233C11.2678 14.4812 9.7815 15.0022 8.25 15C4.524 15 1.5 11.976 1.5 8.25C1.5 4.524 4.524 1.5 8.25 1.5C11.976 1.5 15 4.524 15 8.25C15.0022 9.7815 14.4812 11.2678 13.5233 12.4628ZM12.0187 11.9062C12.9706 10.9274 13.5021 9.61532 13.5 8.25C13.5 5.349 11.1503 3 8.25 3C5.349 3 3 5.349 3 8.25C3 11.1503 5.349 13.5 8.25 13.5C9.61532 13.5021 10.9274 12.9706 11.9062 12.0187L12.0187 11.9062Z" fill="#625C72"/></svg>';

      const reset = document.createElement('button');
      reset.type = 'reset';
      reset.className = 'ais-SearchBox-reset';
      reset.title = 'Effacer la recherche';
      reset.hidden = true;
      reset.innerHTML = '<svg viewBox="0 0 20 20" width="10" height="10" aria-hidden="true"><path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"/></svg>';

      const loading = document.createElement('span');
      loading.className = 'ais-SearchBox-loadingIndicator';
      loading.setAttribute('aria-hidden', 'true');
      loading.hidden = true;
      loading.innerHTML = '<svg width="16" height="16" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg" stroke="#444" aria-hidden="true"><g fill="none" fill-rule="evenodd"><g transform="translate(1 1)" stroke-width="2"><circle stroke-opacity=".5" cx="18" cy="18" r="18" /><path d="M36 18c0-9.94-8.06-18-18-18"><animateTransform attributeName="transform" type="rotate" from="0 18 18" to="360 18 18" dur="1s" repeatCount="indefinite"/></path></g></g></svg>';

      form.appendChild(input);
      form.appendChild(submit);
      form.appendChild(reset);
      
      // Events
      input.addEventListener('input', (e) => {
        const val = (e.target as HTMLInputElement).value;
        refine(val);
      });
      
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const val = (input as HTMLInputElement).value;
        refine(val);
      });
      
      reset.addEventListener('click', (e) => {
        e.preventDefault();
        clear();
        input.value = '';
      });

      wrapperEl.appendChild(form);
      container.appendChild(wrapperEl);
    }

    // update UI on each render
    const inputEl = container.querySelector('.ais-SearchBox-input') as HTMLInputElement | null;
    const resetEl = container.querySelector('.ais-SearchBox-reset') as HTMLButtonElement | null;
    const loadingEl = container.querySelector('.ais-SearchBox-loadingIndicator') as HTMLElement | null;

    if (resetEl) resetEl.hidden = !(query && query.trim().length > 0 && !isSearchStalled);
    if (loadingEl) loadingEl.hidden = !isSearchStalled;
  });

  // Return the widget instance, with a queryHook that only triggers actual search when length > 3
  return customSearchBox({
    container,
    queryHook(query, search) {
      search(query?.length > 3 ? query : '');
    },
  });
};



// Categories
const getConventionCollectiveMenuWidgets = (container: HTMLElement, categoriesOrder: Array<CategoriesOrder>) => {
  function filterAndSortItems(
    items: MenuItem[],
    categoriesOrder: CategoriesOrder[],
  ): MenuItem[] {
    const orderMap = new Map<string, number>(
      categoriesOrder.map(c => [c.label, c.order]),
    );

    return items
      .filter(item => orderMap.has(item.label))
      .sort((a, b) => {
        return (orderMap.get(a.label)! - orderMap.get(b.label)!);
      });
  }
  return menu({
    container,
    attribute: 'category_label',
    transformItems: (items) => {
      return filterAndSortItems(items, categoriesOrder);
    },
    templates: {
      item(data, { html }) {
        const { label, url, cssClasses, isRefined } = data;

        return html`
          <a class="${cssClasses.link} ${isRefined ? 'selected' : ''}" href="${url}" aria-current="${isRefined ? 'true' : 'false'}">
            <span class="${cssClasses.label}">${label}</span>
          </a>
        `;
      },
    },
  });
};

// Clear refinements (custom widget using connectClearRefinements)
const getConventionCollectiveClearRefinements = (container: HTMLElement) => {
  const customClear = connectClearRefinements((renderOptions: any, isFirstRender: boolean) => {
    const { hasRefinements, refine } = renderOptions;

    const iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="9" viewBox="0 0 14 9"><path d="M13.3335 4.90925L13.3336 3.57598H2.55234L5.18547 0.942807L4.24267 0L1.90735e-06 4.24265L4.24267 8.48531L5.18547 7.54245L2.55227 4.90931L13.3335 4.90925Z" fill="black"/></svg>';

    if (isFirstRender) {
      // build DOM nodes with appendChild (no innerHTML)
      const wrapper = document.createElement('div');
      wrapper.className = 'ais-ClearRefinements';

      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', 'Retour');
      button.className = 'ais-ClearRefinements-button ais-ClearRefinements-button--disabled';
      button.disabled = !hasRefinements;

      const iconContainer = document.createElement('span');
      iconContainer.className = 'icon-container';
      // parse SVG and append
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(iconSvg, 'image/svg+xml');
        const svgEl = doc.documentElement as unknown as Node;
        iconContainer.appendChild(svgEl);
      } catch (e) {
        // fallback: set text if DOMParser not available
        iconContainer.textContent = '';
      }

      const label = document.createElement('span');
      label.textContent = hasRefinements ? 'Retour' : '';

      button.appendChild(iconContainer);
      button.appendChild(label);
      wrapper.appendChild(button);
      container.appendChild(wrapper);

      button.addEventListener('click', (e) => {
        e.preventDefault();
        refine();
      });
    } else {
      // update existing nodes
      const button = container.querySelector('.ais-ClearRefinements-button') as HTMLButtonElement | null;
      const label = container.querySelector('.ais-ClearRefinements-button > span:last-child') as HTMLElement | null;
      if (button) {
        button.disabled = !hasRefinements;
        button.classList.toggle('ais-ClearRefinements-button--disabled', !hasRefinements);
      }
      if (label) label.textContent = hasRefinements ? 'Retour' : '';
    }
  });

  return customClear({ container });
};

export {
  getConventionCollectiveHitsWidgets,
  getConventionCollectiveSearchBoxWidgets,
  getConventionCollectiveMenuWidgets,
  getConventionCollectiveClearRefinements,
};